import { isGraphQLName, toGraphQLLiteral } from './gql-literal';

/**
 * GraphQL selection-set and paged-query construction shared by every view.
 *
 * Views differ only in which fields they need and how they order them, so the
 * query shape itself is written once here rather than per view.
 */

/**
 * Build a selection set from (possibly dotted) field paths, nesting object
 * sub-selections to arbitrary depth. For example
 * ``["body", "fromParty.isPhoneOwner", "imageCollectedFilePath.file.s3Url"]``
 * becomes
 * ``body\nfromParty { isPhoneOwner }\nimageCollectedFilePath { file { s3Url } }``.
 */
export function buildFieldSelection(fields: Iterable<string>): string {
  type Trie = { scalars: string[]; children: Map<string, Trie> };

  const root: Trie = { scalars: [], children: new Map() };
  const seenScalar = new Set<string>();

  for (const field of fields) {
    if (!field) continue;
    const segments = field.split('.').filter(Boolean);
    if (segments.length === 0 || segments.some((segment) => !isGraphQLName(segment))) continue;

    if (segments.length === 1) {
      if (seenScalar.has(segments[0])) continue;
      seenScalar.add(segments[0]);
      root.scalars.push(segments[0]);
      continue;
    }

    let node = root;
    for (let i = 0; i < segments.length - 1; i += 1) {
      const segment = segments[i];
      let child = node.children.get(segment);
      if (!child) {
        child = { scalars: [], children: new Map() };
        node.children.set(segment, child);
      }
      node = child;
    }
    const leaf = segments[segments.length - 1];
    if (!node.scalars.includes(leaf) && !node.children.has(leaf)) {
      node.scalars.push(leaf);
    }
  }

  function render(node: Trie, indent: string): string {
    const parts: string[] = [...node.scalars];
    for (const [name, child] of node.children) {
      const body = render(child, `${indent}  `);
      parts.push(`${name} {\n${indent}  ${body}\n${indent}}`);
    }
    return parts.join(`\n${indent}`);
  }

  return render(root, '        ');
}

export interface PagedQueryArgs {
  /** GraphQL operation name; only affects server logs and dev tooling. */
  operationName: string;
  rootQuery: string;
  mobileAcquisitionId: number;
  offset: number;
  limit: number;
  fields: Iterable<string>;
  where?: Record<string, unknown> | null;
  orderBy?: Record<string, unknown> | null;
}

/**
 * One page of an artifact list, selecting exactly the given fields.
 *
 * Argument values are inlined as GraphQL literals for the same reason as
 * {@link buildArtifactListQuery}: input type names vary per artifact and aren't
 * published, so typed variables would need a per-type lookup the ui-config
 * doesn't provide. Field names are dropped unless they are GraphQL names.
 * ``where`` keys must already have been allowlisted by {@link compileWhere}.
 * Filter values are JSON-escaped. The operation name and root field are
 * interpolated bare, so a value that is not a GraphQL name is refused.
 */
export function buildPagedQuery(args: PagedQueryArgs): string | null {
  if (!isGraphQLName(args.operationName) || !isGraphQLName(args.rootQuery)) return null;

  const argParts = [
    `mobileAcquisitionId: ${args.mobileAcquisitionId}`,
    `offset: ${args.offset}`,
    `limit: ${args.limit}`,
  ];
  if (args.where && Object.keys(args.where).length > 0) {
    argParts.push(`where: ${toGraphQLLiteral(args.where)}`);
  }
  if (args.orderBy && Object.keys(args.orderBy).length > 0) {
    argParts.push(`orderBy: ${toGraphQLLiteral(args.orderBy)}`);
  }

  return `query ${args.operationName} {
  ${args.rootQuery}(${argParts.join(', ')}) {
    total
    offset
    limit
    items {
        ${buildFieldSelection(args.fields)}
    }
  }
}`;
}
